import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, CheckCircle, Award, Users, Calendar, Phone } from 'lucide-react';
import { companyInfo, services, testimonials } from '../data/mock';
import Panama from "../Images/Panama.png";
import Panama2 from "../Images/Panama2.webp";
import Panama3 from "../Images/Panama3.webp";
import Panama4 from "../Images/Panama4.webp";
import Panama5 from "../Images/Panama6.webp";

export const Home = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-50 to-gray-100 py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  Building Dreams,
                  <span className="text-orange-600"> Creating Foundations</span>
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Professional construction services for residential homes, renovations, and design projects. 
                  Quality craftsmanship with over {companyInfo.yearsExperience} years of experience.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-orange-700 transition-all duration-200 flex items-center justify-center group"
                >
                  Get Free Quote
                  <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/projects"
                  className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-lg font-semibold hover:border-orange-600 hover:text-orange-600 transition-all duration-200 flex items-center justify-center"
                >
                  View Projects
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 pt-8 border-t border-gray-200">
                <div className="text-center">
                  <p className="text-2xl font-bold text-orange-600">{companyInfo.yearsExperience}</p>
                  <p className="text-sm text-gray-600">Years Experience</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-orange-600">{companyInfo.completedProjects}</p>
                  <p className="text-sm text-gray-600">Projects Completed</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-orange-600">{companyInfo.satisfiedClients}</p>
                  <p className="text-sm text-gray-600">Happy Clients</p>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl p-6 relative z-10">
                <img
                  src={Panama}
                  alt="Modern home construction"
                  className="w-full h-80 object-cover rounded-xl"
                />
                <div className="absolute -bottom-4 -right-4 bg-orange-600 text-white p-4 rounded-xl">
                  <Award size={24} />
                </div>
              </div>
              <div className="absolute top-4 -left-4 w-full h-full bg-orange-100 rounded-2xl -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Our Construction Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From concept to completion, we provide comprehensive construction services 
              tailored to your vision and budget.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => {
              const IconComponent = require('lucide-react')[service.icon];
              return (
                <div key={index} className="group bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all duration-200 hover:bg-white">
                  <div className="bg-orange-100 w-16 h-16 rounded-lg flex items-center justify-center mb-4 group-hover:bg-orange-600 transition-colors duration-200">
                    <IconComponent size={24} className="text-orange-600 group-hover:text-white transition-colors duration-200" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Why Choose Masonry Homes?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                We are a family bsuiness that combines traditional craftsmanship with modern techniques to deliver 
                exceptional results that stand the test of time.
              </p>
              
              <div className="space-y-4">
                {[
                  'Licensed and fully insured contractors',
                  'Quality materials and superior craftsmanship',
                  'Transparent pricing with no hidden costs',
                  'On-time project completion guarantee',
                  'Comprehensive warranty on all work',
                  '24/7 customer support and communication'
                ].map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle size={20} className="text-orange-600 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <img
                src={Panama2}
                alt="Quality construction"
                className="w-full h-48 object-cover rounded-lg"
              />
              <img
                src={Panama3}
                alt="Modern kitchen"
                className="w-full h-48 object-cover rounded-lg mt-8"
              />
              <img
                src={Panama4}
                alt="Home renovation"
                className="w-full h-48 object-cover rounded-lg -mt-8"
              />
              <img
                src={Panama5}
                alt="Finished home"
                className="w-full h-48 object-cover rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600">
              Don't just take our word for it - hear from our satisfied customers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={16} className="text-orange-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4">"{testimonial.text}"</p>
                <div>
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600">{testimonial.project}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-orange-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl text-orange-100 mb-8">
            Contact us today for a free consultation and let's bring your vision to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-orange-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200 flex items-center justify-center group"
            >
              Get Your Free Quote
              <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href={`tel:${companyInfo.phone}`}
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-orange-600 transition-all duration-200 flex items-center justify-center"
            >
              <Phone size={20} className="mr-2" />
              Call Us Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};